/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { Box } from '@mui/material';

import { HUSpin } from '../../components/HUSpin/HUSpin';
import { LinkExpired } from '../../components/LinkExpired/LinkExpired';
import { BasicRoutes } from '../../constants/basicRoutes';
import { passwordRegExp } from '../../constants/regExps';
import { Namespaces } from '../../i18n';
import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { userLogout } from '../../store/auth/actions';
import { ApiClientError } from '../../types/api/ApiClientError';
import { useApiValidation } from '../../utils/useApiValidation';
import { useIsMounted } from '../../utils/useIsMounted';
import { useObjectState } from '../../utils/useObjectState';
import { RecoveryPasswordForm } from './components/RecoveryPasswordForm';
import { createRecoveryPasswordPageStyles } from './RecoveryPasswordPage.styles';
import { RecoveryPasswordFormValues } from './RecoveryPasswordPage.types';

const validationSchema = Yup.object({
  password: Yup.string().required('empty').matches(passwordRegExp, 'invalid_password'),
  confirmPassword: Yup.string()
    .required('empty')
    .oneOf([Yup.ref('password')], 'passwords_no_match'),
  token: Yup.string(),
});

type PathParamsType = {
  token: string;
};

type State = {
  isFetching: boolean;
};

const formInitialValues: RecoveryPasswordFormValues = {
  password: '',
  confirmPassword: '',
  token: '',
};

export const RecoveryPasswordPage = memo(() => {
  const { t } = useTranslation(Namespaces.Login);
  const classes = createRecoveryPasswordPageStyles();
  const [state, setState] = useObjectState<State>({
    isFetching: false,
  });

  const navigate = useNavigate();
  const params = useParams<PathParamsType>();

  const token = params.token!;
  const { isFetching } = state;
  const [isTokenValid, setIsTokenValid] = useState<boolean | undefined>(undefined);
  const dispatch = useDispatch();

  const isMounted = useIsMounted();

  const { setApiError, resetFieldApiError, resetApiError, hasFieldApiError, getFieldApiError, errorMessage } =
    useApiValidation();

  const initialValues: RecoveryPasswordFormValues = useMemo(() => ({ ...formInitialValues, token }), [token]);

  useEffect(() => {
    setIsTokenValid(undefined);
    const fetchData = async () => {
      try {
        await AuthenticationApiService.checkRecoverPasswordToken({
          token: token.trim(),
        });
        if (isMounted.current) {
          setIsTokenValid(true);
        }
      } catch (e) {
        if (isMounted.current) {
          setIsTokenValid(false);
        }
      }
    };
    void fetchData();
  }, [isMounted, token]);

  const submit = async (data: RecoveryPasswordFormValues) => {
    try {
      setState({ isFetching: true });
      resetApiError();
      const params = {
        password: data.password.trim(),
        token: data.token.trim(),
      };

      await AuthenticationApiService.recoverPassword(params);
      if (!isMounted.current) {
        return;
      }
      dispatch(userLogout());
      void navigate(BasicRoutes.LOGIN);
    } catch (e) {
      if (!isMounted.current) {
        return;
      }
      const error = e as ApiClientError;
      if (error.response) {
        setApiError(error);
      }
    } finally {
      if (!isMounted.current) {
        // eslint-disable-next-line no-unsafe-finally
        return;
      }
      setState({ isFetching: false });
    }
  };

  return (
    <div className={classes.root}>
      {isTokenValid === false && <LinkExpired />}
      {(isTokenValid === undefined || isTokenValid) && (
        <HUSpin spinning={isTokenValid === undefined || isFetching} className={classes.wrap}>
          <h3 className={classes.formTitle}>{t('page.recovery_password.title')}</h3>
          <Box className={classes.subTitle}>{t('page.recovery_password.sub_title')}</Box>
          <Box className={classes.subTitle}>{t('page.recovery_password.description')}</Box>
          <Formik<RecoveryPasswordFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={submit}
            enableReinitialize={true}
          >
            {props => (
              <RecoveryPasswordForm
                {...props}
                isFetching={isFetching}
                resetApiError={resetFieldApiError}
                getFieldApiError={getFieldApiError}
                hasFieldApiError={hasFieldApiError}
              />
            )}
          </Formik>
          <Box className={classes.errorBlock}>
            {errorMessage && <p className={classes.errorLine}>{t(`page.recovery_password.error.${errorMessage}`)}</p>}
          </Box>
        </HUSpin>
      )}
    </div>
  );
});
